import { BaseElement } from 'base';
import { bus } from 'bus';
import { backInStock } from 'back-in-stock';
import { isEmail } from 'is-email';
import { NOTIFY_MODAL_TOPIC } from 'notify-modal-topic';
import { ANALYTICS_TOPIC, ANALYTICS_EVENTS } from 'analytics-events';

const TOAST = 'toast:raised';
const BUTTON_TAG = 'action-button';

const FOCUSABLE = [
  "a[href]:not([tabindex='-1'])",
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  "[tabindex]:not([tabindex='-1'])"
]
  .map(selector => `[data-notify-panel] ${selector}`)
  .join(', ');

const subscribeToStock = ({ email, variantId }) =>
  backInStock.subscribe({ email, variantId });

const isRequest = value => {
  const variantId = Number(value?.variantId);
  return Number.isInteger(variantId) && variantId > 0;
};

class NotifyModal extends BaseElement {
  #dialog = null;
  #panel = null;
  #form = null;
  #action = null;
  #shown = null;
  #open = false;
  #opener = null;
  #openerRing = false;
  #submitting = false;
  #invalid = false;

  get topic() {
    return this.dataset.topic || NOTIFY_MODAL_TOPIC;
  }

  run(action) {
    this.#action = typeof action === 'function' ? action : null;
    return this;
  }

  onConnect() {
    this.#dialog = this.q('[data-notify-dialog]');
    this.#panel = this.q('[data-notify-panel]');
    this.#form = this.q('[data-notify-form]');
    if (!this.#dialog || !this.#panel || !this.#form) {
      console.error('[notify-modal] missing dialog, panel or form:', this);
      return;
    }

    this.listen(this.#dialog, 'cancel', this.#onCancel);
    this.listen(this.#dialog, 'close', this.#onDialogClose);
    this.listen(this, 'keydown', this.#onKeydown);
    this.listen(this, 'click', this.#onClick);
    this.listen(this.#form, 'submit', this.#onSubmit);
    this.listen(this.#form, 'input', this.#onInput);

    customElements.whenDefined(BUTTON_TAG).then(() => {
      if (!this.isConnected) return;
      this.q(`${BUTTON_TAG}.notify-modal__continue`)?.run(() =>
        this.requestDismiss()
      );
    });

    this.subscribe(this.topic, this.#onActive);
  }

  onDisconnect() {
    this.#open = false;
    if (this.#dialog?.open) this.#dialog.close();
    this.#shown = null;
    this.#opener = null;
    this.#submitting = false;
  }

  requestDismiss() {
    bus.publish(this.topic, null);
    this.#close();
  }

  get #field() {
    return this.q('text-field.notify-modal__field');
  }

  #onActive(value) {
    if (value != null && !isRequest(value)) {
      console.error(
        `[notify-modal] "${this.topic}" expects { productId, variantId }, received:`,
        value
      );
      this.#close();
      return;
    }
    if (!value) {
      this.#close();
      return;
    }
    if (value === this.#shown) {
      if (!this.#open) this.#show();
      return;
    }

    this.#shown = value;
    this.#reset();
    this.#show();
  }

  #reset() {
    this.#view('form');
    this.#field?.clear?.();
    this.#invalid = false;
  }

  #view(name) {
    this.qa('[data-notify-view]').forEach(view => {
      view.hidden = view.dataset.notifyView !== name;
    });
    const scope = `[data-notify-view='${name}']`;
    const title = this.q(`${scope} [data-notify-title]`);
    const text = this.q(`${scope} [data-notify-text]`);
    if (title) this.#dialog.setAttribute('aria-labelledby', title.id);
    if (text) this.#dialog.setAttribute('aria-describedby', text.id);
    return title;
  }

  #show() {
    if (!this.#open) {
      this.#open = true;
      const active = document.activeElement;
      if (active && active.localName !== 'body' && !this.contains(active)) {
        this.#opener = active;
        this.#openerRing = active.matches(':focus-visible');
      }
    }

    if (this.#dialog.open && !this.#dialog.matches(':modal'))
      this.#dialog.close();
    if (!this.#dialog.open) this.#dialog.showModal();
    this.#panel.focus({ preventScroll: true });
  }

  #close() {
    if (!this.#open) {
      this.#shown = null;
      return;
    }

    this.#open = false;
    if (this.#dialog.open) this.#dialog.close();
    this.#shown = null;

    const opener = this.#opener;
    this.#opener = null;
    if (opener?.isConnected)
      opener.focus?.({ preventScroll: true, focusVisible: this.#openerRing });
  }

  #onCancel(event) {
    event.preventDefault();
    this.requestDismiss();
  }

  #onDialogClose() {
    if (this.#open && !this.#dialog.open) this.requestDismiss();
  }

  #onClick(event) {
    if (
      event.target.closest('[data-notify-scrim]') ||
      event.target.closest('[data-notify-close]')
    )
      this.requestDismiss();
  }

  #onKeydown(event) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      return;
    }
    if (event.key !== 'Tab') return;
    event.stopPropagation();

    const focusable = this.qa(FOCUSABLE).filter(
      element => element.offsetParent !== null
    );
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey) {
      if (active === first || !focusable.includes(active)) {
        event.preventDefault();
        last.focus();
      }
    } else if (active === last || !focusable.includes(active)) {
      event.preventDefault();
      first.focus();
    }
  }

  #onInput() {
    if (!this.#invalid) return;
    this.#field?.clearError?.();
    this.#invalid = false;
  }

  async #onSubmit(event) {
    event.preventDefault();
    if (this.#submitting || !this.#shown) return;
    const field = this.#field;
    if (typeof field?.setError !== 'function') return;

    const email = field.value.trim();
    if (!isEmail(email)) {
      field.setError(this.dataset.invalid ?? '');
      this.#invalid = true;
      field.focus();
      return;
    }

    const shown = this.#shown;
    const { productId, variantId, handle, from } = shown;
    const request = { email, productId, variantId, handle, from };
    const submit = this.q(`${BUTTON_TAG}.notify-modal__submit`);

    this.#submitting = true;
    submit?.setLoading?.(true);
    this.setAttribute('aria-busy', 'true');
    try {
      await (this.#action ?? subscribeToStock)(request);
      this.#track('success');
      if (!this.isConnected || this.#shown !== shown) return;
      this.#view('success')?.focus({ preventScroll: true });
    } catch (error) {
      console.error('[notify-modal] subscription failed:', error);
      this.#track('error');
      const text = this.dataset.error;
      if (this.isConnected && this.#shown === shown && text)
        bus.publish(TOAST, { id: crypto.randomUUID(), text });
    } finally {
      this.#submitting = false;
      submit?.setLoading?.(false);
      this.removeAttribute('aria-busy');
    }
  }

  #track(value) {
    bus.publish(ANALYTICS_TOPIC, {
      id: crypto.randomUUID(),
      event: ANALYTICS_EVENTS.stateChange,
      action: 'notify_submit',
      value,
      source: this.isConnected ? this : null
    });
  }
}

if (!customElements.get('notify-modal'))
  customElements.define('notify-modal', NotifyModal);
